import React from 'react';
import { TreeDeciduous as TreeIcon, Leaf, Droplets, Wind, Ruler, ArrowUp } from 'lucide-react';
import { Tree } from '../../types/tree';
import './TreePopupCard.css';

interface TreePopupCardProps {
  tree: Tree;
}

export const TreePopupCard: React.FC<TreePopupCardProps> = ({ tree }) => {
  const isNative = Number(tree.Native) === 1;
  const isEndemic = Number(tree.Endemism) === 1;
  const isInvasive = Number(tree.Invasive) === 1;

  const getConservationBadge = () => {
    if (isInvasive) return { text: 'Invasive', color: '#e74c3c' };
    if (isEndemic) return { text: 'Endemic', color: '#8e44ad' };
    if (isNative) return { text: 'Native', color: '#27ae60' };
    return { text: 'Non-native', color: '#3498db' };
  };

  const badge = getConservationBadge();

  const getSizeClass = (gbh: number) => {
    if (gbh < 30) return 'Small';
    if (gbh < 100) return 'Medium';
    if (gbh < 200) return 'Large';
    return 'Extra Large';
  };

  const gbh = Number(tree.GBH) || 0;
  const height = Number(tree.Height) || 0;
  const canopySpread = Number(tree.Canopy_spread) || 0;
  const carbon = Number(tree.Carbon) || 0;
  const co2e = Number(tree.co2) || 0;

  return (
    <div className="tree-popup">
      <div className="tree-popup__header">
        <div className="tree-popup__icon">
          <TreeIcon size={24} />
        </div>
        <div className="tree-popup__title-section">
          <h3 className="tree-popup__title">{tree.Species}</h3>
          {tree.Local_Name && (
            <p className="tree-popup__local-name">{tree.Local_Name}</p>
          )}
        </div>
      </div>

      {tree.Common_Name && (
        <div className="tree-popup__common-name">
          {tree.Common_Name}
        </div>
      )}

      <div className="tree-popup__badge" style={{ background: badge.color }}>
        {badge.text}
      </div>

      <div className="tree-popup__metrics">
        <div className="tree-popup__metric">
          <div className="tree-popup__metric-icon">
            <Ruler size={16} />
          </div>
          <div className="tree-popup__metric-content">
            <div className="tree-popup__metric-label">GBH</div>
            <div className="tree-popup__metric-value">{gbh.toFixed(1)} cm</div>
          </div>
        </div>

        <div className="tree-popup__metric">
          <div className="tree-popup__metric-icon">
            <ArrowUp size={16} />
          </div>
          <div className="tree-popup__metric-content">
            <div className="tree-popup__metric-label">Height</div>
            <div className="tree-popup__metric-value">{height.toFixed(1)} m</div>
          </div>
        </div>

        {canopySpread > 0 && (
          <div className="tree-popup__metric">
            <div className="tree-popup__metric-icon">
              <Leaf size={16} />
            </div>
            <div className="tree-popup__metric-content">
              <div className="tree-popup__metric-label">Canopy</div>
              <div className="tree-popup__metric-value">{canopySpread.toFixed(1)} m</div>
            </div>
          </div>
        )}

        {carbon > 0 && (
          <div className="tree-popup__metric">
            <div className="tree-popup__metric-icon">
              <Leaf size={16} />
            </div>
            <div className="tree-popup__metric-content">
              <div className="tree-popup__metric-label">Carbon</div>
              <div className="tree-popup__metric-value">{carbon.toFixed(2)} tonnes</div>
            </div>
          </div>
        )}

        {co2e > 0 && (
          <div className="tree-popup__metric">
            <div className="tree-popup__metric-icon">
              <Droplets size={16} />
            </div>
            <div className="tree-popup__metric-content">
              <div className="tree-popup__metric-label">CO₂e</div>
              <div className="tree-popup__metric-value">{co2e.toFixed(2)} tonnes</div>
            </div>
          </div>
        )}
      </div>

      {tree.Family && (
        <div className="tree-popup__family">
          <strong>Family:</strong> {tree.Family}
        </div>
      )}

      {tree.Location && (
        <div className="tree-popup__location">
          📍 {tree.Location}
        </div>
      )}
    </div>
  );
};

export default TreePopupCard;
